import { useMemo, useState } from "react";
import type { MemberProduct } from "@/types/memberProducts";
import { useMemberProducts } from "@/hooks/useMemberProducts";
import { resumeDeviceSubscription } from "@/api/memberProducts";
import { useIsUserAuthenticated } from "@/contexts/IsUserAuthenticatedContext";
import { buildLoginUrl } from "@/utils/auth";
import { useLocalizedTexts, t } from "@/utils/translations";
import { formatDate } from "@/utils/entitlements";
import styles from "./DeviceSubscriptionPicker.module.css";

export interface DeviceSubscriptionPickerProps {
  /** IMEI of the currently selected device. Null when no device is selected. */
  selectedImei: string | null;
  /** Called with the chosen device's IMEI when the user picks one. */
  onSelectionChange: (imei: string, device: MemberProduct) => void;
}

/**
 * Last-N character mask for IMEIs ("•••••6789"). Returns the raw value if
 * the string is too short to mask meaningfully.
 */
function maskImei(imei: string): string {
  if (imei.length <= 4) return imei;
  const visible = imei.slice(-4);
  return `${"•".repeat(Math.max(4, imei.length - 4))}${visible}`;
}

/**
 * Picker shown above the Add-to-cart button on a device-subscription product.
 * Lets a logged-in user choose which of their devices the subscription will be
 * bound to. Renders one of four states: anonymous (prompts login), loading,
 * empty (no devices on account), or loaded (radio list).
 *
 * Devices with no IMEI, an already-active Stripe subscription, 3rd-party
 * hardware or the customer's own SIM card render in a disabled state with a
 * hint, rather than being filtered out, so the user sees every device they own
 * and understands why they can't select certain ones.
 *
 * A device whose subscription is cancelled but still running can't be selected
 * either — buying again would create a second subscription beside it — but it
 * offers "Resume subscription" instead, which keeps the existing one and its
 * billing date (GROW-394).
 */
export function DeviceSubscriptionPicker({
  selectedImei,
  onSelectionChange,
}: DeviceSubscriptionPickerProps) {
  const texts = useLocalizedTexts();
  // From the layout the app already holds. Fetching it again here rendered a
  // "Loading..." step first, and the picker sits above the Add-to-cart button,
  // so every step moved the button (CLS).
  const authState = useIsUserAuthenticated();
  const isAuthenticated = authState === true;

  // Only fetch member devices when the user is authenticated. Calling the
  // endpoint as a guest would 401 every time and pollute the error state.
  const {
    memberProducts,
    isLoading: isProductsLoading,
    error,
    refetch,
  } = useMemberProducts();
  const [resumingDeviceId, setResumingDeviceId] = useState<string | null>(null);
  const [resumeFailed, setResumeFailed] = useState(false);

  async function handleResume(device: MemberProduct) {
    if (resumingDeviceId) return;
    setResumingDeviceId(device.deviceId);
    setResumeFailed(false);
    const result = await resumeDeviceSubscription(device.deviceId);
    if (result.ok) {
      // The device now reads as renewing; refetch so its hint says so.
      await refetch();
    } else {
      setResumeFailed(true);
    }
    setResumingDeviceId(null);
  }

  const devices = useMemo(
    () => (isAuthenticated ? (memberProducts?.products ?? []) : []),
    [isAuthenticated, memberProducts]
  );

  const heading = (
    <h3 className={styles.heading}>
      {t(
        texts,
        "Tracker.Products.DeviceSubscription.PickerHeading",
        "Choose the device for this subscription"
      )}
    </h3>
  );

  if (authState === null) {
    return (
      <section className={styles.picker} aria-busy="true">
        {heading}
        <p className={styles.statusLine}>
          {t(texts, "Tracker.Generic.Loading", "Loading...")}
        </p>
      </section>
    );
  }

  if (!isAuthenticated) {
    const loginUrl = buildLoginUrl({
      returnPath: window.location.pathname + window.location.search,
      allowGuest: false,
    });
    return (
      <section className={styles.picker}>
        {heading}
        <p className={styles.statusLine}>
          {t(
            texts,
            "Tracker.Products.DeviceSubscription.LoginRequired",
            "Log in to see your devices and choose which one this subscription applies to."
          )}
        </p>
        <a className={styles.primaryButton} href={loginUrl}>
          {t(texts, "Tracker.Generic.Login", "Log in")}
        </a>
      </section>
    );
  }

  if (isProductsLoading) {
    return (
      <section className={styles.picker} aria-busy="true">
        {heading}
        <p className={styles.statusLine}>
          {t(
            texts,
            "Tracker.Products.DeviceSubscription.LoadingDevices",
            "Loading your devices..."
          )}
        </p>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.picker}>
        {heading}
        <p className={styles.errorLine} role="alert">
          {t(
            texts,
            "Tracker.Products.DeviceSubscription.LoadError",
            "Could not load your devices. Please refresh the page."
          )}
        </p>
      </section>
    );
  }

  if (devices.length === 0) {
    return (
      <section className={styles.picker}>
        {heading}
        <p className={styles.statusLine}>
          {t(
            texts,
            "Tracker.Products.DeviceSubscription.NoDevices",
            "You don't have any devices linked to your account yet."
          )}
        </p>
      </section>
    );
  }

  return (
    <section className={styles.picker}>
      {heading}
      {resumeFailed && (
        <p className={styles.errorLine} role="alert">
          {t(
            texts,
            "Tracker.MyAccount.Products.ResumeError",
            "Could not resume the subscription. Please try again."
          )}
        </p>
      )}
      <ul className={styles.deviceList} role="radiogroup">
        {devices.map((device) => {
          const reason = unsupportedReason(device, texts);
          const disabled = reason !== null;
          const checked = !disabled && device.imei === selectedImei;
          const canResume =
            device.hasActiveStripeSubscription &&
            device.cancelAtPeriodEnd &&
            device.canResumeSubscription;
          const isResuming = resumingDeviceId === device.deviceId;
          return (
            <li key={device.id} className={styles.deviceItem}>
              <label
                className={
                  disabled ? styles.deviceLabelDisabled : styles.deviceLabel
                }
              >
                <input
                  type="radio"
                  name="device-subscription-target"
                  className={styles.radio}
                  value={device.imei ?? ""}
                  checked={checked}
                  disabled={disabled}
                  onChange={() => {
                    if (!disabled && device.imei) {
                      onSelectionChange(device.imei, device);
                    }
                  }}
                />
                <span className={styles.deviceTextColumn}>
                  <span className={styles.deviceName}>{device.name}</span>
                  <span className={styles.deviceMeta}>
                    {device.deviceType && (
                      <span className={styles.deviceMetaItem}>
                        {device.deviceType}
                      </span>
                    )}
                    {device.imei && (
                      <span className={styles.deviceMetaItem}>
                        {t(
                          texts,
                          "Tracker.Products.DeviceSubscription.ImeiLabel",
                          "IMEI"
                        )}{" "}
                        {maskImei(device.imei)}
                      </span>
                    )}
                  </span>
                  {reason && (
                    <span className={styles.deviceHint}>{reason}</span>
                  )}
                </span>
              </label>
              {/* Outside the label, so clicking it does not pick the device. */}
              {canResume && (
                <button
                  type="button"
                  className={styles.resumeButton}
                  onClick={() => handleResume(device)}
                  disabled={isResuming}
                >
                  {isResuming
                    ? t(texts, "Tracker.Generic.Working", "Working...")
                    : t(
                        texts,
                        "Tracker.MyAccount.Subscriptions.ResumeSubscription",
                        "Resume subscription"
                      )}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function unsupportedReason(
  device: MemberProduct,
  texts: ReturnType<typeof useLocalizedTexts>
): string | null {
  if (device.isThirdPartyDevice) {
    return t(
      texts,
      "Tracker.MyAccount.Products.ManagedByThirdParty",
      "Managed by 3rd party provider"
    );
  }
  if (device.isOwnSimDevice) {
    return t(texts, "Tracker.MyAccount.Products.OwnSimCard", "Own SIM card");
  }
  if (!device.imei) {
    return t(
      texts,
      "Tracker.Products.DeviceSubscription.MissingImei",
      "Missing IMEI — contact support to set one before subscribing."
    );
  }
  if (device.hasActiveStripeSubscription) {
    if (device.currentPeriodEnd) {
      const datePart = formatDate(device.currentPeriodEnd);
      return device.cancelAtPeriodEnd
        ? t(
            texts,
            "Tracker.Products.DeviceSubscription.CancellingOn",
            "Cancels on"
          ) + ` ${datePart}`
        : t(
            texts,
            "Tracker.Products.DeviceSubscription.ActiveUntil",
            "Active subscription until"
          ) + ` ${datePart}`;
    }
    return t(
      texts,
      "Tracker.Products.DeviceSubscription.AlreadySubscribed",
      "This device already has an active subscription."
    );
  }
  return null;
}

export default DeviceSubscriptionPicker;
